import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { ACCOUNT_TIERS, ACCOUNT_TIER_ORDER, KEEP_CREDITS_PER_YEAR, KEEP_PACKS, KEEP_TYPES, THEME_PRESETS, brandingThemeFromPreset, keepTypeDef } from "@wedding/shared";
import type { AccountTier, DashboardKeepDTO, KeepBranding, KeepType, GalleryCountsResponse, SessionResponse, ThemePreset } from "@wedding/shared";
import { api, ApiError } from "./api";
import { Avatar, Engraved, Icon, Ornament, Script, Toggle, washFor } from "./ui";
import {
  ContributorsSection,
  MomentsSection,
  OverviewSection,
  PhotosSection,
  SettingsSection,
  slugify,
} from "./sections";

// ──────────────── root ────────────────

// Which Keep the URL is pointing at (?keep=<id>). No id → the "Your Keeps" home (AdminEntry).
function keepIdFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("keep");
}

export function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // View router: null → AdminEntry (the shelf of all Keeps); an id → that Keep's dashboard.
  const [keepId, setKeepId] = useState<string | null>(() => keepIdFromUrl());

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.pathname === "/auth/callback") {
      const token = url.searchParams.get("token") ?? "";
      // A fresh magic-link sign-in lands on the home with the create flow open (?new=1) — hosts
      // sign in mostly to start a new Keep.
      api.verifyMagicLink(token).then((s) => { setSession(s); window.history.replaceState(null, "", "/?new=1"); setKeepId(null); })
        .catch(() => window.history.replaceState(null, "", "/")).finally(() => setLoading(false));
      return;
    }
    api.me().then(setSession).catch(() => setSession(null)).finally(() => setLoading(false));
  }, []);

  // Keep the view in sync with the browser Back/Forward buttons.
  useEffect(() => {
    const onPop = () => setKeepId(keepIdFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const openKeep = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("keep", id);
    url.searchParams.delete("new");
    window.history.pushState(null, "", url);
    setKeepId(id);
  };
  const openEntry = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("keep");
    window.history.pushState(null, "", url);
    setKeepId(null);
  };
  async function signOut() { await api.logout().catch(() => {}); setKeepId(null); setSession(null); }

  if (loading) return <div className="auth-wrap"><span className="muted">Loading…</span></div>;
  // A fresh sign-in (email/password) also opens the create flow on the home.
  if (!session) return <AuthScreen onAuthed={(s) => { window.history.replaceState(null, "", "/?new=1"); setKeepId(null); setSession(s); }} />;
  if (keepId) {
    return <AdminApp session={session} setSession={setSession} initialKeepId={keepId}
      openKeep={openKeep} onAllKeeps={openEntry} onSignOut={signOut} />;
  }
  return <AdminEntry session={session} setSession={setSession} openKeep={openKeep} onSignOut={signOut} />;
}

// ──────────────── auth ────────────────

function AuthScreen({ onAuthed }: { onAuthed: (s: SessionResponse) => void }) {
  // Magic link is the primary method; email + password is a secondary opt-in.
  const [mode, setMode] = useState<"magic" | "login">("magic");
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [sent, setSent] = useState(false); const [busy, setBusy] = useState(false);

  async function submit() {
    setErr(""); setBusy(true);
    try {
      if (mode === "login") onAuthed(await api.login({ email, password }));
      else { await api.magicLink(email); setSent(true); }
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Something went wrong"); } finally { setBusy(false); }
  }

  const cta = mode === "login" ? "Sign in" : "Email me a link";
  const disabled = busy || !email.trim() || (mode === "login" && !password);

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <Engraved size={10.5} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.3em" }}>MomentKeep · Admin</Engraved>
        <Script size={44} style={{ color: "var(--c-accent)", marginTop: 2 }}>Welcome back</Script>
        <p className="muted" style={{ marginTop: 6, marginBottom: 20 }}>Collect every photo from life's gatherings — one Keep at a time.</p>
        {sent ? (
          <>
            <p className="ok">Check your email for your sign-in link.</p>
            <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>Sent to {email} — it expires in 30 minutes. <button className="link-a" style={{ background: "none", padding: 0 }} onClick={() => { setSent(false); setErr(""); }}>Use a different email</button></p>
          </>
        ) : (
          <>
            <label className="fl">Email</label>
            <input className="fin" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" onKeyDown={(e) => e.key === "Enter" && !disabled && submit()} />
            {mode === "login" && <><label className="fl">Password</label><input className="fin" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={(e) => e.key === "Enter" && !disabled && submit()} /></>}
            {mode === "magic" && <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>We'll email you a one-time sign-in link — no password needed.</p>}
            {err && <div className="err">{err}</div>}
            <div style={{ marginTop: 16 }}>
              <button className="abtn primary" style={{ width: "100%", justifyContent: "center" }} disabled={disabled} onClick={submit}>{cta}</button>
            </div>
            <p className="auth-switch">
              {mode === "magic"
                ? <button className="link-a" onClick={() => { setMode("login"); setErr(""); }}>Use email &amp; password instead</button>
                : <button className="link-a" onClick={() => { setMode("magic"); setErr(""); }}>Email me a sign-in link instead</button>}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ──────────────── admin shell ────────────────

const NAV = [
  { id: "overview", label: "Dashboard", icon: "dash" },
  { id: "moderation", label: "Photos", icon: "shield" },
  { id: "moments", label: "Moments", icon: "layers" },
  { id: "guests", label: "Contributors", icon: "users" },
  { id: "settings", label: "Settings", icon: "gear" },
];

function AdminApp({ session, setSession, initialKeepId, openKeep, onAllKeeps, onSignOut }: {
  session: SessionResponse; setSession: (s: SessionResponse) => void; initialKeepId: string;
  openKeep: (id: string) => void; onAllKeeps: () => void; onSignOut: () => void;
}) {
  const [keeps, setKeeps] = useState<DashboardKeepDTO[] | null>(null);
  const [section, setSection] = useState("overview");
  const [counts, setCounts] = useState<GalleryCountsResponse | null>(null);
  const [account, setAccount] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // mobile: sidebar drawer open
  const mainRef = useRef<HTMLElement>(null);

  async function reload() {
    const { keeps: list } = await api.listKeeps();
    setKeeps(list);
  }
  useEffect(() => { reload().catch(() => setKeeps([])); }, []);

  // The URL (?keep=) is the source of truth for which Keep is active; fall back to the first.
  const active = keeps?.find((e) => e.id === initialKeepId) ?? keeps?.[0] ?? null;
  useEffect(() => {
    setCounts(null);
    if (active) api.galleryCounts(active.id).then(setCounts).catch(() => {});
  }, [active?.id]);

  const go = (s: string) => { setSection(s); setNavOpen(false); if (mainRef.current) mainRef.current.scrollTop = 0; };

  // refresh session/plan after returning from Stripe Checkout
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("status") === "success") api.me().then(setSession).catch(() => {});
  }, []);

  let body = null;
  if (!keeps) body = <div className="muted" style={{ padding: 40 }}>Loading keeps…</div>;
  else if (!active) body = <FirstEvent session={session} reload={reload} openKeep={openKeep} />;
  else if (section === "overview") body = <OverviewSection ev={active} session={session} go={go} />;
  else if (section === "moderation") body = <PhotosSection ev={active} />;
  else if (section === "moments") body = <MomentsSection ev={active} />;
  else if (section === "guests") body = <ContributorsSection ev={active} />;
  else if (section === "settings") body = <SettingsSection ev={active} session={session} reload={() => reload()} onDeleted={() => { reload().then(onAllKeeps); }} go={go} />;

  const userName = session.user.name || session.organizationName;

  return (
    <div className={"admin" + (navOpen ? " nav-open" : "")}>
      <div className="sidebar-scrim" onClick={() => setNavOpen(false)} />
      <aside className={"sidebar" + (navOpen ? " open" : "")}>
        <button className="back-keeps" onClick={() => { setNavOpen(false); onAllKeeps(); }}><Icon name="arrowleft" size={16} />All Keeps</button>
        <KeepSwitcher keeps={keeps ?? []} active={active} onSelect={(id) => { setNavOpen(false); openKeep(id); }} onAllKeeps={() => { setNavOpen(false); onAllKeeps(); }}
          onCreated={(keep) => { api.me().then(setSession).catch(() => {}); reload(); }} session={session} />
        <nav className="snav">
          {NAV.map((n) => (
            <button key={n.id} className={"snav-item" + (section === n.id ? " on" : "")} onClick={() => go(n.id)} disabled={!active}>
              <Icon name={n.icon} size={19} stroke={section === n.id ? 1.9 : 1.6} />
              <span>{n.label}</span>
              {n.id === "moderation" && counts && counts.pendingPhotos > 0 ? <span className="snav-badge">{counts.pendingPhotos}</span> : null}
            </button>
          ))}
        </nav>
        <div className="sidefoot">
          {active && <a className="view-app" href={active.guestUrl} target="_blank" rel="noreferrer"><Icon name="eye" size={16} /><span>View guest app</span></a>}
          <button className="admin-user" onClick={() => { setNavOpen(false); setAccount(true); }}>
            <Avatar name={userName} seed={7} size={34} />
            <div className="au-text"><span className="au-name">{userName}</span><span className="au-role">{session.user.email ?? session.user.phone}</span></div>
            <Icon name="gear" size={16} style={{ color: "var(--c-ink-soft)", flex: "none" }} />
          </button>
        </div>
      </aside>

      <div className="main-wrap">
        <header className="topbar-admin">
          <button className="nav-toggle" onClick={() => setNavOpen((o) => !o)} aria-label="Menu"><Icon name={navOpen ? "close" : "menu"} size={20} /></button>
          {active && (
            <div className="tb-search" style={{ maxWidth: 460 }}>
              <Icon name="link" size={16} style={{ color: "var(--c-ink-soft)" }} />
              <input readOnly value={active.guestUrl.replace("https://", "")} onFocus={(e) => e.currentTarget.select()} />
              <span className={"evsw-status " + (active.published ? "live" : "upcoming")} style={{ fontSize: 10 }}>{active.published ? "LIVE" : "DRAFT"}</span>
            </div>
          )}
          <div className="tb-right">
            {counts && <div className="tb-stat"><span className="tb-stat-n">{counts.pendingPhotos}</span><span className="tb-stat-l">processing</span></div>}
            {active && <button className="abtn primary" onClick={() => go("moderation")}><Icon name="grid" size={16} />Manage photos</button>}
          </div>
        </header>

        <main className="main" ref={mainRef} key={section + (active?.id ?? "")}>
          {body}
          <footer className="admin-foot"><Ornament color="var(--c-line-strong)" width={120} /><span>MomentKeep · your event, beautifully kept</span></footer>
        </main>
      </div>
      {account && <AccountModal session={session} onClose={() => setAccount(false)} onSignOut={onSignOut} onUpdated={setSession} />}
    </div>
  );
}

// ──────────────── account & settings modal ────────────────

const NOTIF_KEY = "mk_notif_prefs";
const tierPriceLabel = (t: AccountTier) => (t === "free" ? "Free" : "$" + ACCOUNT_TIERS[t].priceCentsYear / 100);

function AccountModal({ session, onClose, onSignOut, onUpdated }: {
  session: SessionResponse; onClose: () => void; onSignOut: () => void; onUpdated: (s: SessionResponse) => void;
}) {
  const [tab, setTab] = useState<"profile" | "plan" | "notif">("profile");
  const [name, setName] = useState(session.user.name ?? "");
  const [email, setEmail] = useState(session.user.email ?? "");
  const [curPw, setCurPw] = useState(""); const [newPw, setNewPw] = useState(""); const [confPw, setConfPw] = useState("");
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const [busy, setBusy] = useState(false);
  const [notif, setNotif] = useState<Record<string, boolean>>(() => {
    try { return { uploads: true, digest: true, flagged: true, milestones: false, marketing: false, ...JSON.parse(localStorage.getItem(NOTIF_KEY) || "{}") }; }
    catch { return { uploads: true, digest: true, flagged: true, milestones: false, marketing: false }; }
  });
  function setN(k: string, v: boolean) { const next = { ...notif, [k]: v }; setNotif(next); localStorage.setItem(NOTIF_KEY, JSON.stringify(next)); }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey); return () => document.removeEventListener("keydown", onKey);
  }, []);

  async function saveProfile() {
    setBusy(true); setMsg({});
    try {
      if (name !== (session.user.name ?? "") || email !== (session.user.email ?? "")) {
        onUpdated(await api.updateProfile({ name, email }));
      }
      if (newPw) {
        if (newPw !== confPw) throw new ApiError("New passwords don't match", 400);
        await api.changePassword({ currentPassword: curPw, newPassword: newPw });
        setCurPw(""); setNewPw(""); setConfPw("");
      }
      setMsg({ ok: "Saved." });
    } catch (e) { setMsg({ err: e instanceof ApiError ? e.message : "Save failed" }); } finally { setBusy(false); }
  }
  async function choosePlan(tier: AccountTier) {
    setBusy(true);
    try {
      if (tier === "free") { const { url } = await api.portal(); window.location.href = url; }
      else { const { url } = await api.subscribe(tier); window.location.href = url; }
    } catch (e) { alert(e instanceof ApiError ? e.message : "Failed"); setBusy(false); }
  }

  const TABS: [typeof tab, string, string][] = [["profile", "Profile", "face"], ["plan", "Plan & billing", "star"], ["notif", "Notifications", "bell"]];

  return (
    <div className="ce-overlay" onMouseDown={onClose}>
      <div className="acct-modal" onMouseDown={(e) => e.stopPropagation()}>
        <header className="acct-head">
          <div>
            <Engraved size={10.5} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.28em" }}>Account &amp; Settings</Engraved>
            <Script size={34} style={{ color: "var(--c-accent)", marginTop: 2 }}>Your account</Script>
          </div>
          <button className="ce-close" onClick={onClose} aria-label="Close"><Icon name="close" size={20} /></button>
        </header>
        <div className="acct-body">
          <nav className="acct-tabs">
            {TABS.map(([id, label, icon]) => (
              <button key={id} className={"acct-tab" + (tab === id ? " on" : "")} onClick={() => setTab(id)}><Icon name={icon} size={18} /><span>{label}</span></button>
            ))}
          </nav>
          <div className="acct-content">
            {tab === "profile" && (
              <div className="acct-pane">
                <div className="acct-avatarrow">
                  <Avatar name={name || email} seed={7} size={64} />
                  <div><span className="au-name">{name || "—"}</span><p className="acct-hint">Signed in as {session.user.email ?? session.user.phone}</p></div>
                </div>
                <div className="acct-field"><label>Full name</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div className="acct-field"><label>Email address</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                <div className="acct-divider"><span>Change password</span></div>
                <div className="acct-field"><label>Current password</label><input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="••••••••" /></div>
                <div className="acct-grid2">
                  <div className="acct-field"><label>New password</label><input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="••••••••" /></div>
                  <div className="acct-field"><label>Confirm new password</label><input type="password" value={confPw} onChange={(e) => setConfPw(e.target.value)} placeholder="••••••••" /></div>
                </div>
              </div>
            )}
            {tab === "plan" && (
              <div className="acct-pane">
                <div className="acct-curbar">
                  <div><span className="acct-curlabel">Current plan</span><span className="acct-curname">{ACCOUNT_TIERS[session.accountTier].label}</span></div>
                  <button className="abtn ghost sm" disabled={busy} onClick={() => choosePlan("free")}>Manage billing</button>
                </div>
                <p className="acct-hint">The Keep Pass is $49/year — {session.keepCredits} Keep{session.keepCredits === 1 ? "" : "s"} left to create. Unused Keeps roll over while your pass stays active.</p>
                <div className="plan-grid">
                  {ACCOUNT_TIER_ORDER.map((id) => {
                    const t = ACCOUNT_TIERS[id];
                    const current = session.accountTier === id;
                    return (
                      <div key={id} className={"plan-card acct-card-pos" + (current ? " current" : "") + (id === "keeppass" ? " feat" : "")}>
                        {id === "keeppass" && <span className="plan-pop">One pass, every gathering</span>}
                        <div className="plan-name">{t.label}</div>
                        <div className="plan-price"><strong>{tierPriceLabel(id)}</strong><span>{id === "free" ? "" : "/ yr"}</span></div>
                        <p className="plan-blurb">{t.blurb}</p>
                        <ul className="plan-feats">{t.features.map((f, i) => <li key={i}><Icon name="check" size={14} stroke={2.2} />{f}</li>)}</ul>
                        <button className={"abtn " + (current ? "ghost" : "primary")} disabled={current || busy} style={{ width: "100%", justifyContent: "center", marginTop: "auto" }} onClick={() => choosePlan(id)}>
                          {current ? "Current plan" : "Switch to " + t.label}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {tab === "notif" && (
              <div className="acct-pane">
                <p className="acct-hint">Saved on this device.</p>
                {[["uploads", "New uploads", "A gentle ping when guests add photos."], ["digest", "Daily digest", "An end-of-day summary of the album."], ["flagged", "Flagged content", "Alert me when a photo needs review."], ["milestones", "Guest milestones", "When a guest is face-matched or joins."], ["marketing", "Product news", "Occasional updates from the team."]].map(([k, t, d]) => (
                  <div className="set-row" key={k} style={{ borderTop: "1px solid var(--c-line)" }}>
                    <div className="set-text"><span className="set-title">{t}</span><span className="set-desc">{d}</span></div>
                    <Toggle on={!!notif[k!]} onChange={(v) => setN(k!, v)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <footer className="acct-foot">
          <button className="acct-signout" onClick={onSignOut}><Icon name="logout" size={16} />Sign out</button>
          <div className="acct-foot-right">
            <button className="abtn ghost" onClick={onClose}>Close</button>
            {tab === "profile" && <button className="abtn primary" disabled={busy} onClick={saveProfile}><Icon name="check" size={16} />{msg.ok ? "Saved" : "Save changes"}</button>}
          </div>
        </footer>
        {(msg.err || msg.ok) && <div style={{ padding: "0 28px 14px" }} className={msg.err ? "err" : "ok"}>{msg.err || msg.ok}</div>}
      </div>
    </div>
  );
}

// first-run empty state
function FirstEvent({ session, reload, openKeep }: { session: SessionResponse; reload: () => Promise<void>; openKeep: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="empty-state" style={{ paddingTop: 80 }}>
      <Script size={40} style={{ color: "var(--c-accent)" }}>Create your first Keep</Script>
      <p>Pick a look, name it, and you'll get a shareable link + QR for your guests.</p>
      <button className="abtn primary" style={{ marginTop: 12 }} onClick={() => setOpen(true)}><Icon name="plus" size={16} />New Keep</button>
      {open && <CreateEventModal session={session} onClose={() => { setOpen(false); reload(); }}
        onCreated={() => {}} onOpenKeep={(id) => { setOpen(false); reload().then(() => openKeep(id)); }} />}
    </div>
  );
}

// ──────────────── event switcher ────────────────

function KeepSwitcher({ keeps, active, onSelect, onAllKeeps, onCreated, session }: {
  keeps: DashboardKeepDTO[]; active: DashboardKeepDTO | null; onSelect: (id: string) => void; onAllKeeps: () => void; onCreated: (keep: DashboardKeepDTO) => void; session: SessionResponse;
}) {
  const [open, setOpen] = useState(false); const [creating, setCreating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const nameOf = (e: DashboardKeepDTO) => e.branding.displayName ?? e.slug;

  return (
    <div className="evsw" ref={ref}>
      <button className={"evsw-trigger" + (open ? " open" : "")} onClick={() => setOpen((o) => !o)}>
        <div className="evsw-id">
          <Engraved size={9.5} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.32em" }}>{session.organizationName} · Admin</Engraved>
          <Script size={32} className="brand-name">{active ? nameOf(active) : "No keeps"}</Script>
          {active && (
            <div className="brand-date">
              <span className="brand-live" style={{ color: active.published ? "var(--c-sage)" : "#b07a2e" }}>
                {active.published && <span className="live-dot2" />}{active.published ? "Event live" : "Draft"}
              </span>
              {active.branding.eventDate && <><span className="dot-sep">·</span>{active.branding.eventDate}</>}
            </div>
          )}
        </div>
        <Icon name={open ? "arrowup" : "arrowdown"} size={16} className="evsw-caret" />
      </button>
      {open && (
        <div className="evsw-menu">
          <button className="evsw-allkeeps" onClick={() => { setOpen(false); onAllKeeps(); }}><Icon name="arrowleft" size={15} />All your Keeps</button>
          <div className="evsw-menu-head">Switch to</div>
          {keeps.map((ev) => (
            <button key={ev.id} className={"evsw-item" + (ev.id === active?.id ? " on" : "")} onClick={() => { onSelect(ev.id); setOpen(false); }}>
              <span className="evsw-thumb" style={ev.branding.copy?.logoUrl ? undefined : washFor(ev.slug.length * 7)}>
                {ev.branding.copy?.logoUrl ? <img src={ev.branding.copy.logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div className="ptile-grain" />}
              </span>
              <span className="evsw-item-text">
                <span className="evsw-item-name">{nameOf(ev)}</span>
                <span className="evsw-item-meta"><span className={"evsw-status " + (ev.published ? "live" : "upcoming")}>{ev.published ? "Live" : "Draft"}</span>{ev.photoCount > 0 && <span> · {ev.photoCount.toLocaleString()} photos</span>}</span>
              </span>
              {ev.id === active?.id && <Icon name="check" size={15} className="evsw-tick" />}
            </button>
          ))}
          <button className="evsw-create" onClick={() => { setCreating(true); setOpen(false); }}><Icon name="plus" size={16} />Create a new event</button>
        </div>
      )}
      {creating && <CreateEventModal session={session} onClose={() => setCreating(false)} onCreated={onCreated} onOpenKeep={(id) => { setCreating(false); onSelect(id); }} />}
    </div>
  );
}

// ──────────────── create-event modal (template picker) ────────────────

// Hero-name display styling for a theme's mini preview (ported from the design).
function presetDisplayStyle(t: ThemePreset): CSSProperties {
  switch (t.display) {
    case "serif": return { fontFamily: '"Newsreader", serif', fontStyle: "italic", fontWeight: 500, fontSize: 26, lineHeight: 1, letterSpacing: "-0.01em", color: t.ink };
    case "sans": return { fontFamily: '"Archivo", sans-serif', fontWeight: 800, fontSize: 21, letterSpacing: "-0.02em", color: t.ink };
    case "serifcaps": return { fontFamily: '"Newsreader", serif', fontWeight: 600, fontSize: 19, textTransform: "uppercase", letterSpacing: "0.12em", color: t.accent };
    case "round": return { fontFamily: '"Yeseva One", serif', fontSize: 23, color: t.ink };
    default: return { fontFamily: '"Newsreader", serif', fontSize: 24, color: t.ink };
  }
}
function presetLabelStyle(t: ThemePreset): CSSProperties {
  return {
    fontFamily: t.display === "sans" ? '"Archivo", sans-serif' : '"Newsreader", serif',
    textTransform: "uppercase", letterSpacing: "0.28em", fontSize: 7.5, fontWeight: 600, color: t.inkSoft,
  };
}

// Line motif drawn in each theme's mini phone preview (ported from the design's _Motif).
function PresetMotif({ t }: { t: ThemePreset }) {
  const { accent, ink } = t;
  if (t.motif === "rule") return (
    <svg viewBox="0 0 184 36" style={{ width: "100%", height: 32 }}>
      <line x1="62" y1="20" x2="122" y2="20" stroke={ink} strokeWidth="1" />
      <circle cx="92" cy="20" r="2.4" fill={accent} />
    </svg>
  );
  if (t.motif === "deco") {
    const rays = [];
    for (let i = -5; i <= 5; i++) { const a = (i / 5) * 0.62; rays.push(<line key={i} x1="92" y1="50" x2={92 + Math.sin(a) * 52} y2={50 - Math.cos(a) * 44} stroke={accent} strokeWidth="0.9" opacity={0.5 + 0.5 * (1 - Math.abs(i) / 5)} />); }
    return <svg viewBox="0 0 184 56" style={{ width: "100%", height: 50 }}>{rays}<path d="M92 50 l8 -9 -8 -5 -8 5 Z" fill={accent} /><line x1="40" y1="52" x2="144" y2="52" stroke={accent} strokeWidth="0.8" /></svg>;
  }
  if (t.motif === "arch") {
    const rays = [];
    for (let i = 0; i < 9; i++) { const a = (i / 8) * Math.PI; rays.push(<line key={i} x1={92 - Math.cos(a) * 34} y1={50 - Math.sin(a) * 34} x2={92 - Math.cos(a) * 42} y2={50 - Math.sin(a) * 42} stroke={accent} strokeWidth="1.4" strokeLinecap="round" />); }
    return <svg viewBox="0 0 184 56" style={{ width: "100%", height: 50 }}><path d="M62 50 A30 30 0 0 1 122 50 Z" fill={t.swatches[1]} />{rays}</svg>;
  }
  if (t.motif === "confetti") {
    const bits: [number, number, string, number?][] = [[34, 40, "l", 20], [52, 24, "c"], [70, 46, "t", 14], [92, 22, "o"], [92, 40, "c"], [108, 44, "t", -18], [126, 24, "l", 40], [144, 42, "c"], [150, 26, "o"]];
    return <svg viewBox="0 0 184 56" style={{ width: "100%", height: 50 }} strokeLinecap="round">{bits.map(([x, y, ty, r = 0], i) => ty === "c" ? <circle key={i} cx={x} cy={y} r="2.2" fill={accent} /> : ty === "o" ? <circle key={i} cx={x} cy={y} r="2.4" fill="none" stroke={accent} strokeWidth="1.2" /> : ty === "t" ? <path key={i} d={`M${x} ${y - 3.4} l3.4 5.6 -6.8 0 Z`} fill="none" stroke={accent} strokeWidth="1.2" transform={`rotate(${r} ${x} ${y})`} /> : <line key={i} x1={x - 3} y1={y - 3} x2={x + 3} y2={y + 3} stroke={accent} strokeWidth="1.5" transform={`rotate(${r} ${x} ${y})`} />)}</svg>;
  }
  if (t.motif === "dots") return (
    <svg viewBox="0 0 184 56" style={{ width: "100%", height: 50 }} fill="none" stroke={accent} strokeLinecap="round">
      <path d="M40 22 Q92 52 144 22" strokeWidth="1.2" />
      {([[40, 22], [66, 34], [92, 38], [118, 34], [144, 22]] as [number, number][]).map(([x, y], i) => <g key={i}><line x1={x} y1={y} x2={x} y2={y + 6} strokeWidth="1" opacity="0.7" /><circle cx={x} cy={y + 9} r="2.2" fill={accent} stroke="none" /></g>)}
    </svg>
  );
  if (t.motif === "sprigstar") {
    const star = [0, 45, 90, 135].map((a, i) => { const rad = a * Math.PI / 180, rr = a % 90 === 0 ? 13 : 7.5; return <line key={i} x1={92 - Math.cos(rad) * rr} y1={30 - Math.sin(rad) * rr} x2={92 + Math.cos(rad) * rr} y2={30 + Math.sin(rad) * rr} stroke={accent} strokeWidth="1.2" strokeLinecap="round" />; });
    const sprig = (x0: number, dir: number) => { const g = [<line key="s" x1={x0} y1="30" x2={x0 + dir * 30} y2="30" stroke={accent} strokeWidth="1.2" strokeLinecap="round" />]; for (let i = 1; i <= 3; i++) { const px = x0 + dir * i * 8; g.push(<line key={"a" + i} x1={px} y1="30" x2={px - dir * 5} y2="24" stroke={accent} strokeWidth="1.1" strokeLinecap="round" />); g.push(<line key={"b" + i} x1={px} y1="30" x2={px - dir * 5} y2="36" stroke={accent} strokeWidth="1.1" strokeLinecap="round" />); } return g; };
    return <svg viewBox="0 0 184 56" style={{ width: "100%", height: 50 }}>{star}{sprig(74, -1)}{sprig(110, 1)}</svg>;
  }
  if (t.motif === "horizon") {
    const rays = [];
    for (let i = 0; i <= 7; i++) { const a = Math.PI * (i / 7); rays.push(<line key={i} x1={92 - Math.cos(a) * 16} y1={42 - Math.sin(a) * 16} x2={92 - Math.cos(a) * 23} y2={42 - Math.sin(a) * 23} stroke={accent} strokeWidth="1.2" strokeLinecap="round" />); }
    return <svg viewBox="0 0 184 56" style={{ width: "100%", height: 50 }} fill="none" stroke={accent} strokeLinecap="round"><line x1="30" y1="42" x2="76" y2="42" strokeWidth="1.2" /><line x1="108" y1="42" x2="154" y2="42" strokeWidth="1.2" /><path d="M76 42 A16 16 0 0 1 108 42" strokeWidth="1.4" />{rays}</svg>;
  }
  return null;
}

function TemplatePhone({ t }: { t: ThemePreset }) {
  const ctaColor = t.id === "little" ? t.ink : t.dark ? t.page : "#fff";
  return (
    <div className="tp-phone" style={{ background: t.paper, color: t.ink }}>
      <div className="tp-status" style={{ color: t.ink }}>
        <span>9:41</span>
        <span className="tp-sigs"><i /><i /><i style={{ opacity: 0.5 }} /></span>
      </div>
      <div className="tp-motif"><PresetMotif t={t} /></div>
      <div className="tp-head">
        <div style={presetLabelStyle(t)}>A MomentKeep</div>
        <div className="tp-names" style={presetDisplayStyle(t)}>{t.sampleNames}</div>
        <div style={{ ...presetLabelStyle(t), letterSpacing: "0.2em", marginTop: 4 }}>{t.sampleDate}</div>
      </div>
      <div className="tp-grid">
        {t.tiles.map((tl, i) => <div key={i} className="tp-tile" style={{ backgroundImage: `linear-gradient(150deg, ${tl[0]}, ${tl[1]})` }} />)}
      </div>
      <div className="tp-cta" style={{ background: t.accent, color: ctaColor }}>Add your photos</div>
    </div>
  );
}

// A sensible default Keep name so most hosts can create in a couple of taps: "{First}'s
// {Gathering} · {Month Year}" (falls back to "My …" when we don't have a name).
function defaultKeepName(session: SessionResponse, preset: ThemePreset): string {
  const first = (session.user.name || session.organizationName || "").trim().split(" ")[0];
  const owner = first ? `${first}'s` : "My";
  const now = new Date();
  return `${owner} ${keepTypeDef(preset.defaultKeepType).label} · ${now.toLocaleString("en-US", { month: "long" })} ${now.getFullYear()}`;
}

function CreateEventModal({ session, onClose, onCreated, onOpenKeep }: {
  session: SessionResponse; onClose: () => void;
  onCreated: (keep: DashboardKeepDTO) => void; onOpenKeep: (id: string) => void;
}) {
  const [sel, setSel] = useState("garden");
  const t = THEME_PRESETS.find((x) => x.id === sel)!;
  const [name, setName] = useState(() => defaultKeepName(session, t));
  const [nameEdited, setNameEdited] = useState(false);
  const [phase, setPhase] = useState<"form" | "creating" | "done">("form");
  const [created, setCreated] = useState<DashboardKeepDTO | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  // Keep the name in step with the chosen theme's gathering until the host edits it.
  useEffect(() => { if (!nameEdited) setName(defaultKeepName(session, t)); /* eslint-disable-next-line */ }, [sel]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && phase !== "creating" && onClose();
    document.addEventListener("keydown", onKey); return () => document.removeEventListener("keydown", onKey);
  }, [phase]);

  async function create() {
    setErr(""); setPhase("creating");
    const displayName = name.trim() || defaultKeepName(session, t);
    const branding: KeepBranding = { displayName, theme: { ...brandingThemeFromPreset(t), density: "lush", momentsLayout: "rows" } };
    const base = slugify(displayName) || "keep";
    // Auto-pick a subdomain from the name; on a collision, try a short suffix so the host never
    // has to think about the URL.
    for (let attempt = 0; attempt < 4; attempt++) {
      const slug = attempt === 0 ? base : `${base}-${Math.random().toString(36).slice(2, 6)}`;
      try {
        const keep = await api.createKeep({ slug, displayName, type: t.defaultKeepType, branding });
        setCreated(keep); onCreated(keep); setPhase("done"); return;
      } catch (e) {
        if (e instanceof ApiError && e.status === 409 && attempt < 3) continue;
        setErr(e instanceof ApiError ? e.message : "Could not create Keep"); setPhase("form"); return;
      }
    }
  }

  function copyLink() {
    if (!created) return;
    navigator.clipboard.writeText(created.guestUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  }

  return (
    <div className="ce-overlay" onMouseDown={() => phase !== "creating" && onClose()}>
      <div className={"ce-modal ce-" + phase} onMouseDown={(e) => e.stopPropagation()}>
        {phase === "creating" ? (
          <div className="ce-status">
            <div className="ce-spinner" style={{ borderTopColor: t.accent }} />
            <Script size={30} style={{ color: "var(--c-accent)" }}>Creating your Keep…</Script>
            <p className="muted">Setting up {name}</p>
          </div>
        ) : phase === "done" && created ? (
          <div className="ce-status ce-done-body">
            <span className="ce-check"><Icon name="check" size={30} stroke={2.6} /></span>
            <Script size={34} style={{ color: "var(--c-accent)" }}>Your Keep is ready</Script>
            <p className="muted" style={{ maxWidth: 340 }}>Share this link with your guests — anyone who has it can add photos.</p>
            <div className="ce-linkbox">{created.guestUrl.replace(/^https?:\/\//, "")}</div>
            <button className="abtn primary ce-copy" onClick={copyLink}><Icon name={copied ? "check" : "link"} size={17} />{copied ? "Copied to clipboard!" : "Copy sharable link"}</button>
            <div className="ce-done-acts">
              <button className="abtn ghost" onClick={() => onOpenKeep(created.id)}>Open dashboard</button>
              <button className="link-a" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <header className="ce-head">
              <div>
                <Engraved size={10.5} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.28em" }}>New Keep</Engraved>
                <Script size={34} style={{ color: "var(--c-accent)", marginTop: 2 }}>Pick a look</Script>
                <p className="ce-sub">Choose a design for the occasion — everything else is set up for you.</p>
              </div>
              <button className="ce-close" onClick={onClose} aria-label="Close"><Icon name="close" size={20} /></button>
            </header>
            <div className="ce-gallery">
              {THEME_PRESETS.map((tpl) => (
                <button key={tpl.id} className={"tcard" + (sel === tpl.id ? " sel" : "")} onClick={() => setSel(tpl.id)}>
                  <div className="tcard-shot">
                    {tpl.isNew && <span className="tcard-new">New</span>}
                    <TemplatePhone t={tpl} />
                    {sel === tpl.id && <span className="tcard-pick"><Icon name="check" size={14} stroke={2.5} /></span>}
                  </div>
                  <div className="tcard-info">
                    <span className="tcard-occ">{tpl.occasion}</span>
                    <div className="tcard-top"><span className="tcard-name">{tpl.name}</span><span className="tcard-sw">{tpl.swatches.map((s, i) => <i key={i} style={{ background: s } as CSSProperties} />)}</span></div>
                    <span className="tcard-tag">{tpl.tag}</span>
                  </div>
                </button>
              ))}
            </div>
            <footer className="ce-foot">
              <div className="ce-namefield">
                <label>Keep name</label>
                <input value={name} onChange={(e) => { setName(e.target.value); setNameEdited(true); }} placeholder="Name your Keep" />
                <span className="ce-linkhint">{(slugify(name) || "your-keep")}.momentkeep.com</span>
              </div>
              {err && <span className="err" style={{ marginTop: 0 }}>{err}</span>}
              <button className="abtn primary ce-create" disabled={!name.trim()} onClick={create}><Icon name="plus" size={17} />Create Keep</button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════ AdminEntry — the host's "Your Keeps" home ════════════════
// Ported from the Claude Design AdminEntry handoff. The post-login landing for a host:
// a shelf of every Keep they've made, a cross-Keep search, the Keep Pass balance, and the
// start/buy-a-Keep flow. Wired to the real api.listKeeps()/session; the prototype's fabricated
// per-Keep fields (venue/contributor counts/live-vs-past status) are derived from what the
// DashboardKeepDTO actually carries, degrading gracefully where a field doesn't exist.

// Accent tone per gathering type (matches the design's TYPE_TONE, keyed to real KeepTypes).
const TYPE_TONE: Record<string, string> = {
  wedding: "var(--c-accent-deep)", birthday: "#b07a2e", reunion: "var(--c-sage)",
  "baby-shower": "#5f8c86", graduation: "#4a6f9c", holiday: "#a8443f",
  party: "#c0663f", other: "var(--c-ink-soft)",
};
const toneFor = (k: DashboardKeepDTO) => TYPE_TONE[k.type] ?? "var(--c-accent-deep)";
const keepName = (k: DashboardKeepDTO) => k.branding.displayName ?? k.slug;
const keepSeed = (k: DashboardKeepDTO) => [...k.slug].reduce((a, c) => a + c.charCodeAt(0), 0);

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Best-effort month/day for the "upcoming" date chip: parse the free-text branding.eventDate
// (e.g. "June 20 · 2026"), else fall back to when the Keep record was created.
function monthDay(k: DashboardKeepDTO): { mon: string; day: string } {
  const src = k.branding.eventDate ?? "";
  const mi = MONTH_ABBR.findIndex((m) => new RegExp(m, "i").test(src));
  const dm = src.match(/\b(\d{1,2})\b/);
  if (mi >= 0 && dm) return { mon: MONTH_ABBR[mi]!.toUpperCase(), day: dm[1]! };
  const d = new Date(k.createdAt);
  if (!Number.isNaN(d.getTime())) return { mon: MONTH_ABBR[d.getMonth()]!.toUpperCase(), day: String(d.getDate()) };
  return { mon: "—", day: "—" };
}

// A little album-spread of washes standing in for cover/recent photos.
function KeepCover({ seed, count, tall }: { seed: number; count: number; tall?: boolean }) {
  return (
    <div className={"kc-cover" + (tall ? " tall" : "")}>
      <span className="kc-tile big" style={washFor(seed)}><i className="ptile-grain" /></span>
      <span className="kc-tile" style={washFor(seed + 4)}><i className="ptile-grain" /></span>
      <span className="kc-tile" style={washFor(seed + 7)}><i className="ptile-grain" /></span>
      <div className="kc-scrim" />
      {count > 0 && <span className="kc-count"><b>{count.toLocaleString()}</b> photos kept</span>}
    </div>
  );
}

// Global cross-Keep search. Presentational for now — a true "search across every Keep" endpoint
// doesn't exist yet (search is per-Keep). The field + scope panel are recreated faithfully.
function AllSearch({ keepCount }: { keepCount: number }) {
  const [focus, setFocus] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setFocus(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const scopes = [
    { ic: "face", h: "A person", p: "“Aunt Carol” finds every photo she's in, across all your Keeps" },
    { ic: "sparkle", h: "A moment", p: "“first dance”, “cake”, “toasts” — matched inside every Keep" },
    { ic: "grid", h: "Anything you remember", p: "“sunset”, “the red dress”, “on the dock” — search sees it all" },
  ];
  return (
    <div className={"allsearch" + (focus ? " open" : "")} ref={ref}>
      <div className="as-field">
        <Icon name="search" size={21} style={{ color: "var(--c-ink-soft)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocus(true)}
          placeholder="Search across every Keep — a face, a moment, a name…" />
        <span className="as-kbd">⌘K</span>
      </div>
      {focus && (
        <div className="as-panel">
          <div className="as-scopes">
            {scopes.map((s) => (
              <div className="as-scope" key={s.h}>
                <span className="as-scope-ic"><Icon name={s.ic} size={17} /></span>
                <div><div className="as-scope-h">{s.h}</div><p>{s.p}</p></div>
              </div>
            ))}
          </div>
          <div className="as-foot">
            <Icon name="sparkle" size={14} />
            One search reaches inside all {keepCount} of your Keep{keepCount === 1 ? "" : "s"} at once — the photo you half-remember is here somewhere.
          </div>
        </div>
      )}
    </div>
  );
}

// The live Keep, given the hero treatment. Stats come from galleryCounts (fetched by the parent).
function LiveKeep({ k, counts, onOpen }: { k: DashboardKeepDTO; counts: GalleryCountsResponse | null; onOpen: () => void }) {
  const pending = counts?.pendingPhotos ?? 0;
  const photos = counts?.totalPhotos ?? k.photoCount;
  const venue = k.branding.eventDate || keepTypeDef(k.type).label;
  const note = k.branding.tagline || (pending > 0
    ? `${pending} new photo${pending === 1 ? "" : "s"} waiting for your review.`
    : "Guests are adding photos — open to see the latest.");
  return (
    <button className="keep-live" onClick={onOpen}>
      <KeepCover seed={keepSeed(k)} count={photos} tall />
      <div className="kl-body">
        <div className="kl-top">
          <span className="kl-live"><span className="live-dot2" />Happening now</span>
          <span className="kl-type" style={{ color: toneFor(k) }}>{keepTypeDef(k.type).label}</span>
        </div>
        <Script size={40} className="kl-name">{keepName(k)}</Script>
        <div className="kl-venue"><Icon name="cal" size={14} />{venue}</div>
        <p className="kl-note">{note}</p>
        <div className="kl-stats">
          <div className="kl-stat"><b>{photos.toLocaleString()}</b><span>photos so far</span></div>
          {counts && <div className="kl-stat"><b>{counts.faceCount.toLocaleString()}</b><span>people found</span></div>}
          <div className="kl-stat warn"><b>{pending}</b><span>to review</span></div>
        </div>
        <div className="kl-acts">
          <span className="ebtn primary"><Icon name="dash" size={16} />Open dashboard</span>
          {pending > 0 && <span className="ebtn alert"><Icon name="shield" size={16} />Review {pending}</span>}
        </div>
      </div>
    </button>
  );
}

// A Keep still in setup (unpublished draft).
function UpcomingKeep({ k, onOpen }: { k: DashboardKeepDTO; onOpen: () => void }) {
  const { mon, day } = monthDay(k);
  return (
    <button className="keep-up" onClick={onOpen}>
      <div className="ku-date"><b>{mon}</b><span>{day}</span></div>
      <div className="ku-body">
        <div className="ku-name">{keepName(k)}</div>
        <div className="ku-meta">{k.branding.eventDate || keepTypeDef(k.type).label}</div>
        <div className="ku-prep"><span className="ku-dot" />Draft · not shared yet</div>
      </div>
      <div className="ku-right">
        <span className="ku-when">{k.branding.eventDate ? "Opens " + k.branding.eventDate : "Set up"}</span>
        <span className="ebtn ghost sm">Set up<Icon name="chevron" size={14} /></span>
      </div>
    </button>
  );
}

// A kept (published) Keep — the album on the shelf.
function KeptKeep({ k, onOpen }: { k: DashboardKeepDTO; onOpen: () => void }) {
  const meta = [k.branding.eventDate, k.guestUrl.replace(/^https?:\/\//, "")].filter(Boolean).join(" · ");
  return (
    <button className="keep-card" onClick={onOpen}>
      <KeepCover seed={keepSeed(k)} count={k.photoCount} />
      <div className="kc-body">
        <div className="kc-tags">
          <span className="kc-type" style={{ color: toneFor(k), borderColor: toneFor(k) }}>{keepTypeDef(k.type).label}</span>
          <span className="kc-kept"><Icon name="check" size={12} stroke={2.4} />Kept forever</span>
        </div>
        <Script size={27} className="kc-name">{keepName(k)}</Script>
        <div className="kc-meta">{meta}</div>
        <div className="kc-foot">
          <span className="kc-stat"><Icon name="grid" size={14} />{k.photoCount.toLocaleString()}</span>
          <span className="kc-open">Open Keep<Icon name="chevron" size={14} /></span>
        </div>
      </div>
    </button>
  );
}

function KSec({ title, sub, count, children }: { title: string; sub?: ReactNode; count?: number; children: ReactNode }) {
  return (
    <section className="ksec">
      <div className="ksec-head">
        <div>
          <Engraved size={11} style={{ letterSpacing: "0.22em", color: "var(--c-ink-soft)" }}>{title}</Engraved>
          {sub && <div className="ksec-sub">{sub}</div>}
        </div>
        {count != null && <span className="ksec-count">{count}</span>}
      </div>
      {children}
    </section>
  );
}

// The out-of-Keeps / buy sheet. Buying an add-on pack starts a one-time Stripe Checkout
// (/api/billing/buy-keeps); the webhook tops up keepCredits once payment clears.
function NewKeepSheet({ onClose }: { onClose: () => void }) {
  const [pack, setPack] = useState("one");
  const [busy, setBusy] = useState(false);
  const sel = KEEP_PACKS.find((p) => p.id === pack)!;

  async function buy() {
    setBusy(true);
    try {
      const { url } = await api.buyKeeps(pack);
      window.location.href = url;
    } catch (e) { alert(e instanceof ApiError ? e.message : "Could not start checkout"); setBusy(false); }
  }

  return (
    <div className="ce-overlay" onMouseDown={onClose}>
      <div className="ns-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ns-head">
          <div>
            <Engraved size={10} style={{ letterSpacing: "0.24em", color: "var(--c-ink-soft)" }}>Add Keeps</Engraved>
            <Script size={30} style={{ color: "var(--c-ink)", marginTop: 4 }}>Need one more?</Script>
          </div>
          <button className="ns-close" onClick={onClose} aria-label="Close"><Icon name="close" size={18} /></button>
        </div>
        <div className="ns-body">
          <div className="ns-explain">
            <span className="ns-explain-ic"><Icon name="refresh" size={16} /></span>
            <p>Your Keep Pass adds <b>{KEEP_CREDITS_PER_YEAR} more Keeps</b> each year — and unused Keeps roll over, so nothing expires. Want to start one before then?</p>
          </div>
          <div className="ns-packs">
            {KEEP_PACKS.map((p) => (
              <button key={p.id} className={"ns-pack" + (pack === p.id ? " on" : "")} onClick={() => setPack(p.id)}>
                <span className="ns-radio">{pack === p.id && <Icon name="check" size={12} stroke={3} />}</span>
                <div className="ns-pack-txt"><b>{p.label}</b>{p.per && <span>{p.per}</span>}</div>
                {p.tag && <span className="ns-pack-tag">{p.tag}</span>}
                <span className="ns-pack-price">${p.priceCents / 100}</span>
              </button>
            ))}
          </div>
          <button className="ebtn primary ns-cta" disabled={busy} onClick={buy}>
            Add {sel.keeps} Keep{sel.keeps > 1 ? "s" : ""} · ${sel.priceCents / 100}
          </button>
          <p className="ns-fine">One-time purchase — these Keeps roll over and never expire, just like your pass Keeps.</p>
        </div>
      </div>
    </div>
  );
}

function AdminEntry({ session, setSession, openKeep, onSignOut }: {
  session: SessionResponse; setSession: (s: SessionResponse) => void; openKeep: (id: string) => void; onSignOut: () => void;
}) {
  const [keeps, setKeeps] = useState<DashboardKeepDTO[] | null>(null);
  const [liveCounts, setLiveCounts] = useState<GalleryCountsResponse | null>(null);
  const [account, setAccount] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [create, setCreate] = useState(false);

  useEffect(() => { api.listKeeps().then((r) => setKeeps(r.keeps)).catch(() => setKeeps([])); }, []);

  const credits = session.keepCredits;
  const hasPass = session.accountTier === "keeppass";
  const first = (session.user.name || session.organizationName || "there").split(" ")[0];

  // Starting a Keep spends a credit via the real create wizard; with none, open the buy sheet.
  const startKeep = () => { if (credits > 0) setCreate(true); else setSheet(true); };

  // Refresh session (credits) + tidy the URL after returning from Stripe, and honor ?new=1.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") api.me().then(setSession).catch(() => {});
    if (params.get("new") === "1") startKeep();
    if (params.get("status") || params.get("new")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("status"); url.searchParams.delete("new");
      window.history.replaceState(null, "", url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bucket the Keeps. Published Keeps are ordered newest-first; the newest is featured as the
  // "Happening now" hero. This is a heuristic — the DTO has no live-vs-past lifecycle field, so
  // until one exists we treat the most recent published Keep as the one currently in motion.
  const published = (keeps ?? []).filter((k) => k.published)
    .slice().sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const upcoming = (keeps ?? []).filter((k) => !k.published);
  const live = published.slice(0, 1);
  const kept = published.slice(1);
  const liveKeep = live[0] ?? null;

  useEffect(() => {
    setLiveCounts(null);
    if (liveKeep) api.galleryCounts(liveKeep.id).then(setLiveCounts).catch(() => {});
  }, [liveKeep?.id]);

  return (
    <div className="entry">
      <header className="entry-top">
        <div className="e-logo">
          <svg width="30" height="30" viewBox="0 0 40 40" aria-hidden="true">
            <rect x="7" y="9" width="22" height="22" rx="5.5" fill="#e6e8d3" transform="rotate(-9 18 20)" />
            <rect x="11" y="9" width="22" height="22" rx="5.5" fill="var(--c-accent-soft)" transform="rotate(7 22 20)" />
            <rect x="9" y="10" width="22" height="22" rx="5.5" fill="var(--c-accent)" />
            <circle cx="20" cy="21" r="4.4" fill="none" stroke="#fff" strokeWidth="2" />
            <circle cx="20" cy="21" r="1" fill="#fff" />
          </svg>
          <span className="e-wordmark">Moment<b>Keep</b></span>
          <span className="e-hosttag">Host</span>
        </div>
        <div className="e-top-right">
          <button className="e-icon" title="Notifications"><Icon name="bell" size={19} /><span className="e-ping" /></button>
          <button className="e-account" onClick={() => setAccount(true)}>
            <Avatar name={session.user.name || session.organizationName} seed={7} size={34} />
            <span className="e-acct-name">{first}</span>
            <Icon name="arrowdown" size={14} style={{ color: "var(--c-ink-faint)" }} />
          </button>
        </div>
      </header>

      <main className="entry-main">
        <section className="greet">
          <Engraved size={10.5} style={{ letterSpacing: "0.3em", color: "var(--c-ink-soft)" }}>Welcome back</Engraved>
          <Script size={54} className="greet-name">Hello again, {first}</Script>
          <p className="greet-sub">Everything you've gathered lives here — kept in full quality, forever.</p>
          <div className={"passchip" + (credits === 0 ? " empty" : "")}>
            <span className="pass-ic"><Icon name="layers" size={16} /></span>
            <div className="pass-main">
              <b>Keep Pass · {hasPass ? "Active" : "Not active"}</b>
              <span className="pass-bal">
                {credits > 0
                  ? <><em>{credits}</em> unused Keep credit{credits > 1 ? "s" : ""}</>
                  : hasPass ? <>No Keeps left — add one to start</> : <>Start a pass to create Keeps</>}
              </span>
            </div>
            {hasPass && (
              <div className="pass-refill">
                <Icon name="refresh" size={14} />
                <span>+{KEEP_CREDITS_PER_YEAR} each year</span>
              </div>
            )}
          </div>
        </section>

        <AllSearch keepCount={keeps?.length ?? 0} />

        {keeps === null ? (
          <div className="muted" style={{ padding: "20px 0" }}>Loading your Keeps…</div>
        ) : (
          <>
            {liveKeep && (
              <KSec title="Happening now" sub={<span className="ksec-sub-txt">This Keep is live — jump in.</span>}>
                <LiveKeep k={liveKeep} counts={liveCounts} onOpen={() => openKeep(liveKeep.id)} />
              </KSec>
            )}

            {upcoming.length > 0 && (
              <KSec title="Coming up" count={upcoming.length}>
                <div className="keep-up-list">
                  {upcoming.map((k) => <UpcomingKeep k={k} key={k.id} onOpen={() => openKeep(k.id)} />)}
                </div>
              </KSec>
            )}

            <KSec title="Past Keeps" sub={<span className="ksec-sub-txt">Your captured moments, browsable any time.</span>} count={kept.length}>
              <div className="keep-grid">
                {kept.map((k) => <KeptKeep k={k} key={k.id} onOpen={() => openKeep(k.id)} />)}
                <button className="keep-new" onClick={startKeep}>
                  <span className="kn-plus"><Icon name="plus" size={22} /></span>
                  <div className="kn-name">Start a new Keep</div>
                  <div className="kn-sub">{credits > 0
                    ? "Uses 1 of your " + credits + " available Keep" + (credits > 1 ? "s" : "")
                    : "You're out — add a Keep to start one"}</div>
                </button>
              </div>
            </KSec>
          </>
        )}

        <footer className="entry-foot">
          <Ornament color="var(--c-line-strong)" width={130} />
          <span>One pass. Every gathering. Kept for good.</span>
        </footer>
      </main>

      {sheet && <NewKeepSheet onClose={() => setSheet(false)} />}
      {create && <CreateEventModal session={session} onClose={() => setCreate(false)}
        onCreated={(keep) => { api.me().then(setSession).catch(() => {}); setKeeps((prev) => (prev ? [keep, ...prev.filter((k) => k.id !== keep.id)] : [keep])); }}
        onOpenKeep={(id) => { setCreate(false); openKeep(id); }} />}
      {account && <AccountModal session={session} onClose={() => setAccount(false)} onSignOut={onSignOut} onUpdated={setSession} />}
    </div>
  );
}
